<template>
  <div class="upload-file">
    <h1 class="ellipsis">{{ $t('aurora.switchingTracking.modalTitle') }}</h1>
    <a-form
      class="upload-file__form"
      @submit="handleUpload"
    >
      <div class="upload-file__inputs">
        <div class="upload-file__inputs-label">
          <h3>{{ $t('base.placeholder.selectField', { name: 'файл' }) }}</h3>
          <a-upload
            :fileList="fileList"
            accept=".csv,.xlsx,.xls"
            :remove="handleRemove"
            :beforeUpload="beforeUpload"
          >
            <div class="upload-file__attachment">
              <a-button>
                <a-icon type="upload" />
                {{ $t('base.placeholder.upload') }}
              </a-button>
            </div>
          </a-upload>
        </div>
        <div class="upload-file__inputs-label">
          <h3>{{ $t('base.placeholder.selectField', { name: 'лейбл' }) }}</h3>
          <a-select
            v-model="selectedLabel"
            class="label__select"
            :placeholder="$t('base.placeholder.selectField', { name: 'лейбл' })"
            size="small"
            mode="single"
            dropdownClassName="input-enum__dropdown"
            allowClear
          >
            <a-select-option
              v-for="option of labelsList"
              :key="option.id"
            >
              {{ option.name }}
            </a-select-option>
          </a-select>
        </div>
      </div>
      <a-button
        type="primary"
        :disabled="fileList.length === 0 || !selectedLabel || uploadDisabled"
        :loading="uploading"
        @click="handleUpload"
      >
        {{ $t('base.placeholder.upload') }}
      </a-button>
    </a-form>
    <div class="upload-file__result">
      <p
        v-if="error"
        class="upload-file__result_error"
      >
        {{ error }}
      </p>
      <div
        v-if="result.length"
        class="upload-file__result_table"
      >
        <a-table
          :columns="columns"
          :data-source="result"
          :pagination="{
            hideOnSinglePage: true,
            pageSize: 10,
          }"
          :scroll="{ y: 300 }"
        >
          <span
            slot="error"
            slot-scope="text"
            class="upload-file__result_error"
          >
            {{ text }}
          </span>
        </a-table>
      </div>
    </div>
    <a-button
      v-if="fileName"
      type="primary"
      style="margin-top: 12px"
      @click="goToResult"
    >
      {{ $t('aurora.switchingTracking.goToPage') }}
    </a-button>
  </div>
</template>

<script>
import { bus } from '@/helpers';
import GET_LABELS_LIST from '@/queries/auroraCustomQueries/getLabelsList';

const debounce = 1000;

export default {
  name: 'UploadFile',
  components: {},

  props: {},

  apollo: {
    labelsList: {
      ...GET_LABELS_LIST,
      loadingKey: 'loadingLabelQuery',
      debounce,
      client: 'newApiClient',
      update(data) {
        return data?.Label?.list ?? [];
      },
    },
  },

  data() {
    return {
      fileList: [],
      uploading: false,
      selectedLabel: null,
      result: [],
      error: null,
      fileName: null,
      uploadDisabled: false,
    };
  },

  computed: {
    isFileAttached() {
      return this.fileList?.length > 0;
    },
    columns() {
      return [
        {
          title: this.$t('aurora.switchingTracking.upc'),
          dataIndex: 'upc',
          key: 'upc',
        },
        {
          title: this.$t('aurora.switchingTracking.status'),
          dataIndex: 'status',
          key: 'status',
        },
        {
          title: this.$t('aurora.switchingTracking.error'),
          dataIndex: 'error',
          key: 'error',
          scopedSlots: { customRender: 'error' },
        },
      ];
    },
  },

  watch: {
    fileList() {
      this.uploadDisabled = false;
    },
    selectedLabel() {
      this.uploadDisabled = false;
    },
  },

  methods: {
    handleRemove(file) {
      const index = this.fileList.indexOf(file);
      const newFileList = this.fileList.slice();
      newFileList.splice(index, 1);
      this.fileList = newFileList;
    },
    beforeUpload(file) {
      this.fileList = [file];
      return false;
    },
    handleUpload() {
      this.error = null;
      if (!this.fileList.length || !this.selectedLabel) {
        return null;
      }
      this.$emit('preventClosing', true);
      this.result = [];
      this.uploadDisabled = true;
      const formData = new FormData();
      this.fileList.forEach((file) => {
        formData.append('file', file);
      });
      formData.append('labelId', this.selectedLabel);
      this.uploading = true;

      try {
        if (!navigator.onLine) {
          this.error = this.$t('app.error.offlineError') ?? 'Нет соединения с интернетом';
          this.uploading = false;
          this.$emit('preventClosing', false);
          return;
        }

        fetch(`/api/s3/upload/switching_tracking/xls`, {
          method: 'POST',
          body: formData,
        })
          .then(async (response) => {
            if (response.status !== 200) {
              const errorText = await response.text();
              throw new Error(errorText);
            }

            return response.json();
          })
          .then((data) => {
            this.result = data.map((el) => ({
              upc: el.upc,
              status: this.$t(`aurora.switchingTracking.statuses.${el.status}`) ?? el.status,
              error: el.error,
            }));
            this.fileName = data[0].file;
            if (data.every((el) => el.error === null)) {
              bus.$emit('error', {
                type: 'success',
                message: this.$t('base.importSuccess'),
              });
            }
          })
          .catch((error) => {
            console.error(error.message);
            this.error = error.message;
          })
          .finally(() => {
            this.uploading = false;
            this.$emit('preventClosing', false);
          });
      } catch (e) {
        this.onUploadError(e.message);
        this.$emit('preventClosing', false);
      }
    },
    onUploadError(message) {
      this.emitError(this.$t('base.importError'), message);
    },

    goToResult() {
      const route = this.$router.resolve({
        path: '/page/SwitchingTracking',
        query: {
          f_file: `[ILIKE.${this.fileName}`,
        },
      });
      window.open(route.href, '_blank');
    },
  },
};
</script>

<style lang="scss">
.upload-file {
  width: 60vw;
  &__inputs {
    & > * {
      display: grid;
      grid-template-columns: 1fr;

      p {
        margin-bottom: 8px;
        width: fit-content;
      }
      margin-bottom: 12px;
    }

    &-date {
      .datepicker {
        width: 100%;
        height: fit-content;
        .filter-timepicker {
          margin-bottom: 10px;
          display: block;
        }
      }
    }

    &-label {
      .ant-select {
        width: 300px;
      }

      &__dropdown.ant-select-dropdown {
        top: 32px !important;

        .ant-select-dropdown-menu {
          max-height: 5 * 32px;
        }
      }
      .ant-select-selection--multiple .ant-select-selection__placeholder {
        margin-left: 0;
      }
    }
  }
  &__result {
    margin-top: 12px;

    &_error {
      color: red;
    }

    .ant-table .ant-table-body {
      @include scrollbars();
    }
  }
}

@media (max-width: $tabletBreakpoint) {
  .upload-file {
    width: 80vw;
    min-width: auto;
  }
}
</style>
